/**
 * refinementUtils
 *
 * Pure functions used by the AI Refine workflow:
 *   • computeWordDiff  → word-level diff for the "what changed" view
 *   • countWords       → simple word counter (used everywhere)
 *   • extractSpecifics → proper nouns / years / numbers / quoted phrases /
 *                         currency / awards detection — used by the
 *                         faithfulness warning to detect lost evidence
 *   • findLostEvidence → which specifics from `original` are missing in `refined`
 *   • suggestRefinementWords → target word count given criterion + question type
 *
 * Zero external deps — keeps the bundle small and the logic auditable.
 */

export type DiffOp = 'equal' | 'added' | 'removed';

export interface DiffSegment {
  op: DiffOp;
  text: string;
}

/** Tokenise on word boundaries while preserving punctuation as separate tokens
 *  so "didn't" stays one token but "case." becomes ["case", "."]. */
function tokenise(s: string): string[] {
  if (!s) return [];
  // Split on whitespace; keep contiguous non-space runs as tokens.
  // Also split punctuation off the trailing edge so "case." vs "case" align.
  const out: string[] = [];
  for (const raw of s.split(/(\s+)/)) {
    if (!raw) continue;
    if (/^\s+$/.test(raw)) {
      out.push(raw);
      continue;
    }
    // Strip trailing punctuation characters into separate tokens
    const m = raw.match(/^(.*?)([.,;:!?")\]]*)$/);
    if (m && m[1]) {
      // Also lead punctuation
      const leadM = m[1].match(/^([("[{]*)(.*)$/);
      if (leadM && leadM[1]) out.push(leadM[1]);
      if (leadM && leadM[2]) out.push(leadM[2]);
      else out.push(m[1]);
      if (m[2]) out.push(m[2]);
    } else {
      out.push(raw);
    }
  }
  return out.filter((t) => t.length > 0);
}

/** Standard LCS-table word diff. O(n*m) memory; fine for the few-thousand-word
 *  bodies we deal with in tender responses. Not the fastest implementation, but
 *  trivially correct and dependency-free. */
export function computeWordDiff(a: string, b: string): DiffSegment[] {
  const A = tokenise(a);
  const B = tokenise(b);
  const n = A.length;
  const m = B.length;

  // Bail on pathological inputs to avoid burning memory.
  if (n === 0 && m === 0) return [];
  if (n === 0) return [{ op: 'added', text: B.join('') }];
  if (m === 0) return [{ op: 'removed', text: A.join('') }];
  if (n * m > 800_000) {
    // Too large for naive LCS; fall back to a chunked diff: split by paragraph.
    return chunkedParagraphDiff(a, b);
  }

  const dp: Uint16Array[] = new Array(n + 1);
  for (let i = 0; i <= n; i++) dp[i] = new Uint16Array(m + 1);
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (A[i - 1] === B[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Walk back to build the diff
  const ops: DiffSegment[] = [];
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    if (A[i - 1] === B[j - 1]) {
      ops.push({ op: 'equal', text: A[i - 1] });
      i--; j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      ops.push({ op: 'removed', text: A[i - 1] });
      i--;
    } else {
      ops.push({ op: 'added', text: B[j - 1] });
      j--;
    }
  }
  while (i > 0) {
    ops.push({ op: 'removed', text: A[--i] });
  }
  while (j > 0) {
    ops.push({ op: 'added', text: B[--j] });
  }
  ops.reverse();

  // Coalesce consecutive segments of the same op for cleaner rendering.
  const merged: DiffSegment[] = [];
  for (const seg of ops) {
    const last = merged[merged.length - 1];
    if (last && last.op === seg.op) {
      last.text += seg.text;
    } else {
      merged.push({ op: seg.op, text: seg.text });
    }
  }
  return merged;
}

/** Fallback for very large inputs: diff paragraph-by-paragraph so we don't
 *  exhaust memory on full-document refinements. Still readable. */
function chunkedParagraphDiff(a: string, b: string): DiffSegment[] {
  const aChunks = a.split(/(\n{2,})/);
  const bChunks = b.split(/(\n{2,})/);
  const out: DiffSegment[] = [];
  const n = Math.max(aChunks.length, bChunks.length);
  for (let i = 0; i < n; i++) {
    const ac = aChunks[i] || '';
    const bc = bChunks[i] || '';
    if (ac === bc) {
      out.push({ op: 'equal', text: ac });
    } else {
      // Each paragraph diffed in isolation
      const sub = computeWordDiff(ac, bc);
      out.push(...sub);
    }
  }
  return out;
}

/** Plain word counter (whitespace-delimited; markdown / punctuation safe). */
export function countWords(s: string): number {
  if (!s) return 0;
  const m = s.trim().match(/\S+/g);
  return m ? m.length : 0;
}

export interface SpecificEvidence {
  /** The literal token from the source text. */
  token: string;
  /** Category bucket — drives icon/colour in the UI. */
  kind:
    | 'proper_noun'
    | 'year'
    | 'number'
    | 'currency'
    | 'percentage'
    | 'award'
    | 'acronym'
    | 'quote';
}

const AWARD_WORDS = /\b(BAFTA|NASA|Emmy|Webby|D&AD|Cannes|Lions|Oscar|Pulitzer|Wired)\b/g;
const ACRONYM = /\b[A-Z]{2,}(?:\d+)?\b/g; // BBC, NCSL, UNCRC, GDPR, B2B, IPR, KS1
const YEAR = /\b(19|20)\d{2}\b/g;
const CURRENCY = /[£$€¥]\s?\d[\d,]*(?:\.\d+)?(?:\s?(?:m|million|bn|billion|k|thousand))?\b/gi;
const PERCENTAGE = /\b\d+(?:\.\d+)?\s?%/g;
const NUMBER_WITH_UNIT = /\b\d+(?:[\d,.]*)?\s?(?:pages|years|days|hours|months|weeks|users|projects|cases|clients|contracts)\b/gi;
const QUOTED = /"[^"\n]{2,80}"|'[^'\n]{2,80}'/g;
// Multi-word proper-noun runs ("Wentworth Woodhouse", "Glasgow Science Centre",
// "Live from Space"). Lowercase function words allowed inside.
const PROPER_NOUN_RUN =
  /\b[A-Z][a-zA-Z]+(?:\s+(?:[A-Z][a-zA-Z]+|of|the|and|for|on|at|in)){1,5}\b/g;

/** Extract evidence-specific tokens from text. Used to detect lost facts.
 *  Deduplicates case-insensitively but keeps original casing of first hit. */
export function extractSpecifics(text: string): SpecificEvidence[] {
  if (!text) return [];
  const seen = new Map<string, SpecificEvidence>();

  const push = (token: string, kind: SpecificEvidence['kind']) => {
    const key = token.toLowerCase().trim();
    if (!key || key.length < 2) return;
    if (!seen.has(key)) seen.set(key, { token: token.trim(), kind });
  };

  for (const m of text.match(AWARD_WORDS) || []) push(m, 'award');
  for (const m of text.match(ACRONYM) || []) push(m, 'acronym');
  for (const m of text.match(YEAR) || []) push(m, 'year');
  for (const m of text.match(CURRENCY) || []) push(m, 'currency');
  for (const m of text.match(PERCENTAGE) || []) push(m, 'percentage');
  for (const m of text.match(NUMBER_WITH_UNIT) || []) push(m, 'number');
  for (const m of text.match(QUOTED) || []) push(m, 'quote');
  for (const m of text.match(PROPER_NOUN_RUN) || []) push(m, 'proper_noun');

  return Array.from(seen.values());
}

/** Specifics present in `original` but absent from `refined`. The refined-text
 *  comparison is case-insensitive and substring-based so paraphrases like
 *  "Wentworth Woodhouse Hall" still match against "Wentworth Woodhouse". */
export function findLostEvidence(original: string, refined: string): SpecificEvidence[] {
  const refinedLc = (refined || '').toLowerCase();
  const origSpecifics = extractSpecifics(original || '');
  return origSpecifics.filter((s) => !refinedLc.includes(s.token.toLowerCase()));
}

/** Convert a page-limit (from extracted compliance items) into an approximate
 *  word-count budget. Industry standard for proposal-grade A4 prose at 11pt
 *  Calibri with 1.15 line-spacing is roughly 400-500 words/page; we use 450
 *  as a defensible mid-point. */
export function estimateWordBudgetFromPages(pages: number): number {
  if (!Number.isFinite(pages) || pages <= 0) return 0;
  return Math.round(pages * 450);
}

/** Best-effort parse of "no more than N pages", "60 pages including annexes",
 *  "maximum of 30 pages", etc. from a piece of text. Returns null if no
 *  page constraint is found. */
export function extractPageLimitFromText(text: string | undefined | null): number | null {
  if (!text) return null;
  const patterns = [
    /(?:no\s+more\s+than|up\s+to|maximum\s+of|not\s+exceed(?:ing)?|within|limited\s+to)\s+(\d+)\s+pages?/i,
    /(\d+)\s+pages?\s+(?:including|excluding|maximum|or\s+less)/i,
    /(?:overall\s+length|length\s+of\s+the\s+response).*?(\d+)\s+pages?/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m[1]) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n) && n > 0 && n < 1000) return n;
    }
  }
  return null;
}

/** Suggested target word count for a question. Mirrors the heuristic in
 *  dataTransformers.ts so both the matrix and the per-question card agree. */
export function suggestRefinementWords(
  type?: string,
  criterionLabel?: string
): number | undefined {
  const t = (type || '').toLowerCase();
  let base = 350;
  if (t.includes('must')) base = 500;
  else if (t.includes('should')) base = 350;
  else if (t.includes('could')) base = 200;
  else if (t.includes('pass')) base = 250;
  if (criterionLabel) {
    const m = criterionLabel.match(/(\d+(?:\.\d+)?)\s*%/);
    if (m) {
      const pct = parseFloat(m[1]);
      if (Number.isFinite(pct)) {
        const multiplier = Math.max(0.5, Math.min(1.6, pct / 20));
        base = Math.round(base * multiplier);
      }
    }
  }
  return Math.round(base / 50) * 50;
}

/** Prompt presets for the HumanRefinementModal. Each chip pre-fills the
 *  textarea with a directive that matches the most common bid-writer intents.
 *  Order is intentional: most-used at the top. */
export const REFINE_PROMPT_PRESETS: Array<{ id: string; label: string; prompt: string; }> = [
  {
    id: 'concise',
    label: 'Make more concise',
    prompt:
      'Tighten this response by ~20% — preserve every concrete fact, name, project, award, date, and number, but remove filler, hedging, and repeated phrasing. Aim for direct, active-voice sentences.',
  },
  {
    id: 'evidence',
    label: 'Add more evidence',
    prompt:
      'Strengthen this response with concrete evidence drawn from the company knowledge base — named projects, clients, dates, contract values, award outcomes, measurable results. Do not invent facts; only surface evidence the company already has.',
  },
  {
    id: 'preserve',
    label: 'Restore lost evidence',
    prompt:
      'You may have removed specific evidence (named projects, awards, dates, numbers, case studies) when refining. Re-introduce every concrete claim from the original, woven into the body. Do not add a closing summary paragraph.',
  },
  {
    id: 'criterion',
    label: 'Strengthen criterion alignment',
    prompt:
      'Re-write this so it more clearly addresses the tender\'s evaluation criterion for this question. Weave the criterion framing into the body of the response, not as a closing tag. Cite the criterion\'s specific requirements where the response addresses them.',
  },
  {
    id: 'thesis',
    label: 'Lead with a thesis sentence',
    prompt:
      'Rewrite the opening so the first sentence directly answers the question with a one-line thesis statement. The body then evidences and elaborates. Avoid generic openings like "We are pleased to..." or "Yes.".',
  },
  {
    id: 'tighten-words',
    label: 'Tighten to target word count',
    prompt:
      'Cut this response to its target word count. Preserve every concrete claim, every named project, every award, every number. Drop hedging and repetition. If shortening would lose evidence, keep the evidence.',
  },
  {
    id: 'tone',
    label: 'More confident tone',
    prompt:
      'Increase confidence and active voice. Remove hedges (likely, perhaps, hopefully, we believe, we hope). Switch passive constructions to active. Preserve all original facts and meaning.',
  },
];
